// filepath: c:\Users\makar\projects\report-notes\src\pages\reports\CreateReportButton.js
import React, {useState} from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import * as ICRData from '../collection-requests/_test_data/ICRequests.js';
import {useIcrSubscription} from '../../firebase/firestore/useIcrSubscription';
import {createReport} from '../../firebase/firestore/reports';

import {useNavigate} from "react-router";
import {textToQuery} from '../../utils/api/segments';
import {resolveIcrNumber} from '../../firebase/firestore/icr';

const resolveIcrListNumber = (icr) => resolveIcrNumber(icr) || icr?.id || '';

const PRODUCT_TYPES = [
    {value: 'pitch', label: 'Pitch'},
    {value: 'analytic', label: 'Analytic Product'},
    {value: 'tactical_sitrep', label: 'Tactical SITREP'},
    {value: 'strategic_sitrep', label: 'Strategic SITREP'},
];

const getProductTypeObj = (productType) => PRODUCT_TYPES.find((type) => type.value === productType) || {
    value: productType,
    label: productType,
};

const buildBasePayload = ({title, topic, defaultQuery, productTypeObj, user}) => ({
    title,
    type: 'REPORT',
    topic,
    defaultQuery: defaultQuery || '',
    productType: productTypeObj,
    userId: user.uid,
    userDisplayName: user.displayName || 'Anonymous',
    status: 'Draft',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
});

const buildBaseMeta = ({title, productTypeObj}) => ({
    title,
    productType: productTypeObj,
    dtg: '',
    originator: {value: '', label: 'Select originator'},
    classification: {value: 'U', label: 'U - Unclassified'},
    distribution: [],
    compartment: [],
    confidence: {value: '', label: 'Select confidence'},
    ifc: {value: '', label: 'Select IFC'},
    productionCenter: {value: '', label: 'Select production center'}
});

const buildPitchPayload = ({title, topic, defaultQuery, user, productTypeObj}) => ({
    ...buildBasePayload({title, topic, defaultQuery, productTypeObj, user}),
    meta: {
        ...buildBaseMeta({title, productTypeObj}),
    }
});

const buildAnalyticPayload = ({title, topic, defaultQuery, user, productTypeObj, selectedICR}) => ({
    ...buildBasePayload({title, topic, defaultQuery, productTypeObj, user}),
    meta: {
        ...buildBaseMeta({title, productTypeObj}),
        icrNumber: selectedICR || null,
    }
});

const buildTacticalSitrepPayload = ({title, topic, defaultQuery, user, productTypeObj, selectedICR}) => ({
    ...buildBasePayload({title, topic, defaultQuery, productTypeObj, user}),
    meta: {
        ...buildBaseMeta({title, productTypeObj}),
        icrNumber: selectedICR || null,
    }
});

const buildStrategicSitrepPayload = ({title, topic, defaultQuery, user, productTypeObj, selectedICR}) => ({
    ...buildBasePayload({title, topic, defaultQuery, productTypeObj, user}),
    meta: {
        ...buildBaseMeta({title, productTypeObj}),
        icrNumber: selectedICR || null,
    }
});

const buildDefaultDocInitPayload = () => ({
    sourcePackage: {
        type: 'ymap',
        payload: {
            folders: {},
            items: {},
        },
    },
});

// Keep separate builders so product-specific _doc_init can evolve independently.
const buildPitchDocInitPayload = () => buildDefaultDocInitPayload();
const buildAnalyticDocInitPayload = () => buildDefaultDocInitPayload();
const buildTacticalSitrepDocInitPayload = () => buildDefaultDocInitPayload();
const buildStrategicSitrepDocInitPayload = () => buildDefaultDocInitPayload();

/**
 * CreateReportButton
 * - Renders a primary button "Create New Product"
 * - On click, opens a dialog to collect Product Name and Topic
 * - Topic field is shown with a grey placeholder: "Describe what your report is about"
 * - On submit, creates a report document and calls onCreated(reportId)
 */
const CreateReportButton = ({user, icrList: icrListProp, icrLoading: icrLoadingProp, icrError: icrErrorProp}) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [productName, setProductName] = useState('');
    const [topic, setTopic] = useState('');
    const [productType, setProductType] = useState('analytic');
    const [selectedICR, setSelectedICR] = useState('');
    const isPitch = productType === 'pitch';
    // ICR list is provided by the parent (DraftsPage) to avoid duplicate fetches.
    // Fall back to fetching internally only when no list prop is supplied.
    const hasIcrProp = icrListProp !== undefined;
    const {data: icrListFetched, loading: icrLoadingFetched, error: icrErrorFetched} =
        useIcrSubscription({limit: 500, skip: hasIcrProp});
    const icrList = hasIcrProp ? icrListProp : icrListFetched;
    const icrLoading = hasIcrProp ? icrLoadingProp : icrLoadingFetched;
    const icrError = hasIcrProp ? icrErrorProp : icrErrorFetched;

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        if (submitting) return;
        setOpen(false);
    };

    const handleSubmit = async () => {
        if (!user || submitting) return;
        const title = productName?.trim();
        const topicValue = topic?.trim() || '';
        if (!title) return; // simple guard; keep button disabled if empty
        if (isPitch && !topicValue) return;
        if (!isPitch && !selectedICR) return;

        try {
            setSubmitting(true);
            // Compute defaultQuery once (backend + fallback)
            const defaultQuery = await getQueryKeywords(topicValue);

            const productTypeObj = getProductTypeObj(productType);
            const payloadParams = {
                title,
                topic: topicValue,
                defaultQuery,
                user,
                productTypeObj,
                selectedICR,
            };

            const payloadByType = {
                pitch: buildPitchPayload(payloadParams),
                analytic: buildAnalyticPayload(payloadParams),
                tactical_sitrep: buildTacticalSitrepPayload(payloadParams),
                strategic_sitrep: buildStrategicSitrepPayload(payloadParams),
            };

            const docInitByType = {
                pitch: buildPitchDocInitPayload(),
                analytic: buildAnalyticDocInitPayload(),
                tactical_sitrep: buildTacticalSitrepDocInitPayload(),
                strategic_sitrep: buildStrategicSitrepDocInitPayload(),
            };

            const payload = payloadByType[productType] || buildPitchPayload(payloadParams);
            const docInitPayload = docInitByType[productType] || {};

            const {reportId} = await createReport(payload, docInitPayload);
            // Use stored defaultQuery to build query string
            const queryString = defaultQuery ? `?query=${encodeURIComponent(defaultQuery)}&aiSearch=` : '?aiSearch=';
            navigate(`/products/${reportId}/edit${queryString}`);
            // reset fields for the next use
            setProductName('');
            setTopic('');
            setProductType('analytic');
            setOpen(false);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to create product', e);
        } finally {
            setSubmitting(false);
        }
    };

    const canSubmit = !!productName.trim()
        && (isPitch ? !!topic.trim() : !!selectedICR)
        && !submitting;

    // Auto-generate topic from selected ICR: subject, informationRequirement, eei.what, eei.where
    const handleSelectICR = (icrNumber) => {
        setSelectedICR(icrNumber);
        if (!icrNumber) {
            setTopic('');
            return;
        }
        const icr = icrList.find((item) => resolveIcrListNumber(item) === icrNumber);
        if (!icr) return;
        const parts = [icr.subject, icr.informationRequirement, icr.eei && icr.eei.what, icr.eei && icr.eei.where];
        setTopic(parts.filter(Boolean).join(' '));
    };

    return (
        <>
            <Button variant="contained" onClick={handleOpen} disabled={submitting}>
                Create New Product
            </Button>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Create New Product</DialogTitle>
                <DialogContent>
                    <Stack mt={1} spacing={2}>
                        <TextField
                            label="Product Name"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                            autoFocus
                            fullWidth
                            disabled={submitting}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="product-type-label">Product Type</InputLabel>
                            <Select
                                labelId="product-type-label"
                                label="Product Type"
                                variant="outlined"
                                value={productType}
                                onChange={(e) => setProductType(e.target.value)}
                                disabled={submitting}
                            >
                                {PRODUCT_TYPES
                                    .filter(pt => pt.value === 'pitch')
                                    .map(pt => <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>)}
                                <Divider component="li"/>
                                {PRODUCT_TYPES
                                    .filter(pt => pt.value !== 'pitch')
                                    .map(pt => <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>)}
                            </Select>
                        </FormControl>

                        {!isPitch && (
                            <FormControl fullWidth required>
                                <InputLabel id="select-icr-label">Select ICR</InputLabel>
                                <Select
                                    labelId="select-icr-label"
                                    label="Select ICR"
                                    variant="outlined"
                                    value={selectedICR}
                                    onChange={(e) => handleSelectICR(e.target.value)}
                                    disabled={submitting || icrLoading}
                                >
                                    {((icrList && icrList.length > 0) ? icrList : (ICRData.ICR_TEST_DATA || [])).map(icr => {
                                        const number = resolveIcrListNumber(icr);
                                        return (
                                        <MenuItem key={number || icr.id} value={number}>
                                            <Stack>
                                                <Typography>{icr.subject}</Typography>
                                                <Typography variant="caption"
                                                            color="text.secondary">{number}</Typography>
                                            </Stack>
                                        </MenuItem>
                                        );
                                    })}
                                </Select>
                                {icrLoading &&
                                    <Typography variant="caption" color="text.secondary">Loading ICRs…</Typography>}
                                {icrError &&
                                    <Typography variant="caption" color="error">Failed to load ICRs</Typography>}
                            </FormControl>
                        )}

                        <TextField
                            label="Topic"
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder="Describe what your report is about"
                            fullWidth
                            multiline
                            minRows={2}
                            required={isPitch}
                            disabled={submitting}
                            // placeholder text is rendered in grey by default in MUI
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
                        {submitting ? 'Creating…' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export async function getQueryKeywords(topic) {
    const trimmedTopic = (topic || '').trim();
    if (!trimmedTopic) return '';

    try {
        const {query} = await textToQuery(trimmedTopic);
        if (query && query.trim()) return query.trim();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('textToQuery failed, falling back to raw topic as query', e);
    }
    // textToQuery failed or returned an empty query: fall back to the topic text itself so the
    // report is always persisted with a non-empty defaultQuery. This prevents the loader from
    // later treating defaultQuery as missing and seeding the Sources box from the report name.
    return trimmedTopic;
}

export default CreateReportButton;
